import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MetaApiService } from './meta-api.service';
import { Broker } from './broker.services';
import { BrokerAdapter, BrokerOrder, BrokerPosition } from './broker-adapter.interface';

/**
 * BrokerAdapter implementation for MetaAPI (MT4 / MT5).
 * Delegates all HTTP calls to MetaApiService.
 *
 * MetaAPI field mapping reference:
 *   accountInformation.balance    → Efectivo
 *   accountInformation.equity     → ValorLiquidativo
 *   accountInformation.margin     → MargenTotal
 *   accountInformation.freeMargin → FondosDisponible
 *   accountInformation.credit     → PoderAdquisitivo (credit + equity)
 */
@Injectable({
  providedIn: 'root'
})
export class MetaApiBrokerAdapterService implements BrokerAdapter {

  constructor(private metaApiService: MetaApiService) {}

  isConnected(): Observable<boolean> {
    return this.metaApiService.connected$;
  }

  // ---------------------------------------------------------------------------
  // Account summary
  // ---------------------------------------------------------------------------

  getAccountSummary(accountId: string): Observable<Broker> {
    return this.metaApiService.getAccountInformation(accountId, true).pipe(
      map((data: any) => {
        // MetaAPI devuelve HTTP 200 con body de error cuando el terminal MT no está conectado
        if (data?.error && data?.message) {
          throw { metaApiTerminalError: true, message: data.message };
        }
        return this.mapAccountSummary(data);
      }),
      catchError(err => {
        // HTTP 504 TimeoutError: el terminal MT no respondió (no conectado al broker o reconectando)
        if (err?.error?.error === 'TimeoutError') {
          return throwError(() => ({ metaApiTerminalError: true, message: err.error.message }));
        }
        if (this.metaApiService.isTransientError(err)) {
          return throwError(() => ({ metaApiTransient: true, message: 'MetaAPI no respondió a tiempo' }));
        }
        console.error('[MetaApiBrokerAdapter] getAccountSummary error', err);
        return throwError(() => err);
      })
    );
  }

  private mapAccountSummary(data: any): Broker {
    if (!data) {
      return { PoderAdquisitivo: 0, ValorLiquidativo: 0, MargenTotal: 0, FondosDisponible: 0, Efectivo: 0 };
    }
    return {
      PoderAdquisitivo: (data.equity ?? 0) + (data.credit ?? 0),
      ValorLiquidativo: data.equity    ?? 0,
      MargenTotal:      data.margin    ?? 0,
      FondosDisponible: data.freeMargin ?? 0,
      Efectivo:         data.balance   ?? 0
    };
  }

  // ---------------------------------------------------------------------------
  // Positions
  // ---------------------------------------------------------------------------

  getPositions(accountId: string): Observable<BrokerPosition[]> {
    return this.metaApiService.getPositions(accountId).pipe(
      map((data: any[]) => (Array.isArray(data) ? data : []).map(p => this.mapPosition(p))),
      catchError(err => {
        console.error('[MetaApiBrokerAdapter] getPositions error', err);
        return of([]);
      })
    );
  }

  private mapPosition(p: any): BrokerPosition {
    return {
      symbol:        p.symbol ?? '',
      conid:         p.id,
      quantity:      p.volume ?? 0,
      avgCost:       p.openPrice ?? 0,
      marketPrice:   p.currentPrice ?? 0,
      marketValue:   (p.volume ?? 0) * (p.currentPrice ?? 0),
      unrealizedPnl: p.unrealizedProfit ?? p.profit ?? 0,
      realizedPnl:   p.realizedProfit ?? 0,
      raw: p
    };
  }

  // ---------------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------------

  getOrders(accountId: string): Observable<BrokerOrder[]> {
    return this.metaApiService.getOrders(accountId).pipe(
      map((data: any[]) => (Array.isArray(data) ? data : []).map(o => this.mapOrder(o))),
      catchError(err => {
        console.error('[MetaApiBrokerAdapter] getOrders error', err);
        return of([]);
      })
    );
  }

  private mapOrder(o: any): BrokerOrder {
    const total     = o.volume ?? 0;
    // In MetaAPI, currentVolume is the REMAINING (unfilled) volume
    const remaining = o.currentVolume ?? total;
    return {
      orderId:        String(o.id ?? ''),
      symbol:         o.symbol ?? '',
      side:           (o.type ?? '').includes('SELL') ? 'SELL' : 'BUY',
      orderType:      this.mapOrderType(o.type),
      quantity:       total,
      filledQuantity: total - remaining,
      limitPrice:     o.openPrice,
      stopPrice:      o.stopLoss,
      status:         o.state ?? '',
      raw: o
    };
  }

  private mapOrderType(t: string): 'LIMIT' | 'MARKET' | 'STOP' {
    const upper = (t ?? '').toUpperCase();
    if (upper.includes('LIMIT'))  return 'LIMIT';
    if (upper.includes('STOP'))   return 'STOP';
    return 'MARKET';
  }

  // ---------------------------------------------------------------------------
  // Place / Cancel order
  // ---------------------------------------------------------------------------

  placeOrder(accountId: string, order: any): Observable<any> {
    return this.metaApiService.placeOrder(accountId, order);
  }

  cancelOrder(accountId: string, orderId: string): Observable<any> {
    return this.metaApiService.cancelOrder(accountId, orderId);
  }
}
